import React, { useCallback, useEffect, useState } from 'react';
import { Loader, Message, Pagination } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';

import { API, showError } from '../../helpers';
import PageSizeSelectV8, {
  readPageSizeV8,
} from '../../components/PageSizeSelectV8';
import {
  DataSection,
  LedgerTable,
  StatementTable,
  SummaryBand,
} from '../Billing';
import BillingDateSelectionV89 from '../Billing/BillingDateSelectionV89';
import RecordExportV1023 from '../../components/RecordExportV1023';

const OrganizationBillingBookV8 = ({ admin, accountId, billingDay = 1 }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPageSize, setLedgerPageSize] = useState(() =>
    readPageSizeV8('v8-page-size-organization-ledger', 50),
  );
  const [statements, setStatements] = useState([]);
  const [statementPage, setStatementPage] = useState(1);
  const [statementTotal, setStatementTotal] = useState(0);
  const [statementPageSize, setStatementPageSize] = useState(() =>
    readPageSizeV8('v8-page-size-organization-statements', 20),
  );

  const load = useCallback(async () => {
    if (admin && !/^\d+$/.test(String(accountId || ''))) return;
    setLoading(true);
    try {
      const base = admin
        ? `/api/billing/v8/admin/accounts/${accountId}`
        : '/api/billing/v8/self';
      const [summaryResponse, ledgerResponse, statementResponse] =
        await Promise.all([
          API.get(`${base}/summary`),
          API.get(
            `${base}/ledger?page=${ledgerPage}&page_size=${ledgerPageSize}`,
          ),
          API.get(
            `${base}/statements?page=${statementPage}&page_size=${statementPageSize}`,
          ),
        ]);
      if (!summaryResponse || !ledgerResponse || !statementResponse) return;
      if (!summaryResponse.data?.success)
        throw new Error(summaryResponse.data?.message);
      if (!ledgerResponse.data?.success)
        throw new Error(ledgerResponse.data?.message);
      if (!statementResponse.data?.success)
        throw new Error(statementResponse.data?.message);

      const statementData = statementResponse.data?.data;
      setSummary(summaryResponse.data.data);
      setEntries(ledgerResponse.data?.data?.entries || []);
      setLedgerTotal(Number(ledgerResponse.data?.data?.total || 0));
      setStatements(
        Array.isArray(statementData)
          ? statementData
          : statementData?.statements || [],
      );
      setStatementTotal(
        Number(
          Array.isArray(statementData)
            ? statementResponse.data?.total || 0
            : statementData?.total || 0,
        ),
      );
    } catch (error) {
      showError(error.message || t('billing.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [
    accountId,
    admin,
    ledgerPage,
    ledgerPageSize,
    statementPage,
    statementPageSize,
    t,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loader active inline='centered' />;
  if (!summary?.configured)
    return <Message info content={t('billing.not_configured')} />;

  return (
    <div className='organization-billing-book-v8'>
      <div className='record-export-toolbar-v1023 record-export-ledger-v1023'>
        <RecordExportV1023 kind='ledger' accountId={accountId || summary.account?.id || ''} admin={admin} />
      </div>
      <SummaryBand summary={summary} internal={admin} t={t} />
      <BillingDateSelectionV89
        endpoint={
          admin
            ? `/api/billing/v8/admin/accounts/${accountId}/date-selection-summary`
            : '/api/billing/v8/self/date-selection-summary'
        }
        billingDay={billingDay}
      />
      <DataSection
        title={t('billing.ledger.title')}
        empty={entries.length ? '' : t('billing.ledger.empty')}
      >
        <LedgerTable entries={entries} internal={admin} t={t} />
        <div className='list-pagination-v8'>
          <PageSizeSelectV8
            label={t('log.table.page_size')}
            storageKey='v8-page-size-organization-ledger'
            value={ledgerPageSize}
            onChange={(nextPageSize) => {
              setLedgerPage(1);
              setLedgerPageSize(nextPageSize);
            }}
          />
          {ledgerTotal > ledgerPageSize ? (
            <Pagination
              activePage={ledgerPage}
              totalPages={Math.ceil(ledgerTotal / ledgerPageSize)}
              onPageChange={(_, { activePage }) =>
                setLedgerPage(Number(activePage))
              }
            />
          ) : null}
        </div>
      </DataSection>
      <DataSection
        title={t('billing.statement.title')}
        empty={statements.length ? '' : t('billing.statement.empty')}
      >
        <StatementTable statements={statements} internal={admin} t={t} />
        <div className='list-pagination-v8'>
          <PageSizeSelectV8
            label={t('log.table.page_size')}
            storageKey='v8-page-size-organization-statements'
            value={statementPageSize}
            onChange={(nextPageSize) => {
              setStatementPage(1);
              setStatementPageSize(nextPageSize);
            }}
          />
          {statementTotal > statementPageSize ? (
            <Pagination
              activePage={statementPage}
              totalPages={Math.ceil(statementTotal / statementPageSize)}
              onPageChange={(_, { activePage }) =>
                setStatementPage(Number(activePage))
              }
            />
          ) : null}
        </div>
      </DataSection>
    </div>
  );
};

export default OrganizationBillingBookV8;
